import React from 'react';
import { Link } from 'react-router-dom';
import { MyUnitCodeDto } from '../../types/unit';
import { BookOpen, Play, GraduationCap } from 'lucide-react';

interface UnitCardProps {
  unit: MyUnitCodeDto;
}

const UnitCard: React.FC<UnitCardProps> = ({ unit }) => {
  const getGradeInArabic = (grade: string) => {
    switch (grade) {
      case 'Secondary1':
        return 'أولى ثانوي';
      case 'Secondary2':
        return 'ثانية ثانوي';
      case 'Secondary3':
        return 'ثالثة ثانوي';
      default:
        return grade;
    }
  };

  const getTermInArabic = (term: string) => {
    switch (term) {
      case 'First':
        return 'الترم الأول';
      case 'Second':
        return 'الترم الثاني';
      default:
        return term;
    }
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-gold-400/40 hover:shadow-lg hover:shadow-[#001848]/8">
      <div className="h-1 w-full bg-gradient-to-l from-gold-400 via-gold-500 to-[#001848]" />

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#001848] text-white">
            <BookOpen className="h-5 w-5" />
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold sm:text-xs ${
              unit.active
                ? 'border border-orange-200 bg-orange-50 text-orange-700'
                : 'border border-slate-200 bg-slate-50 text-slate-500'
            }`}
          >
            {unit.active ? 'نشط' : 'غير نشط'}
          </span>
        </div>

        <h3 className="mb-2 line-clamp-2 text-base font-bold leading-snug text-[#001848] font-cairo-bold sm:text-lg">
          {unit.unitName}
        </h3>

        {unit.courseName ? (
          <div className="mb-3 flex items-center gap-1.5 text-slate-500">
            <GraduationCap className="h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-1 text-xs sm:text-sm">{unit.courseName}</span>
          </div>
        ) : (
          <div className="mb-3 flex items-center gap-1.5 text-gold-600">
            <BookOpen className="h-3.5 w-3.5 shrink-0" />
            <span className="text-xs font-medium sm:text-sm">حصة مستقلة</span>
          </div>
        )}

        {(unit.grade || unit.term) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {unit.grade && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-[#001848] sm:text-xs">
                {getGradeInArabic(unit.grade)}
              </span>
            )}
            {unit.term && (
              <span className="rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 sm:text-xs">
                {getTermInArabic(unit.term)}
              </span>
            )}
          </div>
        )}

        <Link
          to={`/unit-viewer/${unit.unitId}`}
          onClick={(e) => e.stopPropagation()}
          className="mt-auto inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-gold-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-gold-400"
        >
          دخول الحصة
          <Play className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
};

export default React.memo(UnitCard);
